import { useRef, useState, useEffect } from "react";
import { motion, useInView } from "framer-motion";
import { Quote, TrendingUp, TrendingDown, ArrowRight } from "lucide-react";

const TEAL = "#00A8B3";
const RED = "#FF4D4F";
const GREEN = "#28A745";
const NAVY = "#1A1F36";
const MID = "#6B7280";

const fade = {
  hidden: { opacity: 0, y: 24 },
  visible: (i = 0) => ({ opacity: 1, y: 0, transition: { delay: i * 0.12, duration: 0.6, ease: [0.25, 0.46, 0.45, 0.94] } }),
};

const letterAnim = {
  hidden: { opacity: 0, y: 20 },
  visible: (i) => ({ opacity: 1, y: 0, transition: { delay: i * 0.03, duration: 0.4 } }),
};

const AnimatedNumber = ({ value, suffix = "", inView, delay = 0 }) => {
  const [display, setDisplay] = useState(null);
  const num = parseFloat(value);
  useEffect(() => {
    if (!inView || isNaN(num)) return;
    setDisplay(0);
    const timeout = setTimeout(() => {
      let start = 0;
      const dur = 1800;
      const step = num / (dur / 16);
      const timer = setInterval(() => {
        start += step;
        if (start >= num) { setDisplay(num); clearInterval(timer); }
        else setDisplay(Math.floor(start));
      }, 16);
      return () => clearInterval(timer);
    }, delay);
    return () => clearTimeout(timeout);
  }, [inView, num, delay]);
  if (isNaN(num)) return <span>{value}</span>;
  if (display === null) return <span style={{ opacity: 0.3 }}>{num}{suffix}</span>;
  return <span>{display}{suffix}</span>;
};

const AnimatedText = ({ text, className, style, inView }) => (
  <span className={className} style={style}>
    {text.split("").map((char, i) => (
      <motion.span key={i} custom={i} variants={letterAnim} initial="hidden" animate={inView ? "visible" : "hidden"}
        style={{ display: 'inline-block' }}>
        {char === " " ? "\u00A0" : char}
      </motion.span>
    ))}
  </span>
);

const personas = [
  { name: "Suresh", role: "Hospital Admin", img: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=100&h=100&fit=crop&crop=face",
    quote: "Compliance isn't an afterthought anymore. Audit trails are complete and staff don't fight over paper records." },
  { name: "Dr. Ravi", role: "General Physician", img: "https://images.unsplash.com/photo-1612349317150-e413f6a5b16d?w=100&h=100&fit=crop&crop=face",
    quote: "Complete patient history in seconds. No more asking 'what medications are you on?' repeatedly." },
  { name: "Ananya", role: "Patient", img: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=face",
    quote: "I control who sees my health data. Sharing records with a new doctor is just an OTP away." },
];

const impactStats = [
  { label: "Consult Time Saved", beforeVal: 8, afterVal: 6, beforeLabel: "8 min", afterLabel: "6 min", maxVal: 10, beforePct: 80, afterPct: 60, icon: TrendingDown, improvement: "25% faster" },
  { label: "Patient Context", beforeVal: 40, afterVal: 95, beforeLabel: "40%", afterLabel: "95%", maxVal: 100, beforePct: 40, afterPct: 95, icon: TrendingUp, improvement: "2.4x more" },
  { label: "Claim Rejections", beforeVal: 15, afterVal: 5, beforeLabel: "15%", afterLabel: "5%", maxVal: 20, beforePct: 75, afterPct: 25, icon: TrendingDown, improvement: "67% less" },
];

export const TestimonialsSection = () => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: "-80px" });

  return (
    <section id="insights" className="py-20 lg:py-28" style={{ background: 'linear-gradient(180deg, #F0F8FF 0%, #F5F5F0 100%)' }} ref={ref}>
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        {/* Animated heading */}
        <motion.div variants={fade} initial="hidden" animate={inView ? "visible" : "hidden"} className="text-center mb-12">
          <motion.span initial={{ opacity: 0, scale: 0.8 }} animate={inView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }} className="text-spaced mb-3 block">
            W H A T &nbsp; P E O P L E &nbsp; S A Y
          </motion.span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-heading font-bold mb-4" style={{ color: NAVY }}>
            <AnimatedText text="User Insights & Impact" inView={inView} />
          </h2>
        </motion.div>

        {/* Impact stats with PROPORTIONAL before/after bars */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5 mb-14">
          {impactStats.map((s, i) => (
            <motion.div key={s.label}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ delay: i * 0.15 + 0.2, duration: 0.6 }}
            >
              {/* Animated border card */}
              <div className="relative rounded-2xl p-[2px] overflow-hidden" style={{
                background: `linear-gradient(135deg, ${TEAL}40, transparent 40%, transparent 60%, ${TEAL}40)`,
                backgroundSize: '200% 200%',
                animation: `borderShift ${4 + i}s ease infinite`,
              }}>
                <div className="bg-white rounded-2xl p-6">
                  <div className="flex items-center justify-between mb-2">
                    <motion.h4 initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: i * 0.15 + 0.4 }}
                      className="text-sm font-heading font-bold" style={{ color: NAVY }}>{s.label}</motion.h4>
                    <motion.div animate={inView ? { rotate: [0, 10, -10, 0] } : {}} transition={{ delay: i * 0.3 + 1, duration: 0.6 }}>
                      <s.icon className="w-4 h-4" style={{ color: TEAL }} />
                    </motion.div>
                  </div>

                  {/* Improvement badge */}
                  <motion.div initial={{ opacity: 0, x: -10 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: i * 0.2 + 0.8 }}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold mb-4"
                    style={{ background: `${GREEN}15`, color: GREEN }}>
                    <ArrowRight className="w-3 h-3" /> {s.improvement}
                  </motion.div>

                  {/* Before bar - PROPORTIONAL */}
                  <div className="mb-3">
                    <div className="flex justify-between text-xs mb-1.5">
                      <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: i * 0.2 + 0.5 }}
                        className="font-semibold" style={{ color: RED }}>Before</motion.span>
                      <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: i * 0.2 + 0.7 }}
                        className="font-bold text-sm" style={{ color: NAVY }}>
                        <AnimatedNumber value={s.beforeVal} suffix={s.beforeLabel.includes("min") ? " min" : "%"} inView={inView} delay={i * 200 + 500} />
                      </motion.span>
                    </div>
                    <div className="h-3 rounded-full" style={{ background: '#FEE2E2' }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${s.beforePct}%` } : {}}
                        transition={{ duration: 1.2, delay: i * 0.2 + 0.6, ease: "easeOut" }}
                        className="h-full rounded-full relative overflow-hidden"
                        style={{ background: RED }}
                      >
                        <motion.div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)' }}
                          animate={{ x: ['-100%', '100%'] }} transition={{ duration: 2, repeat: Infinity, delay: 2 }} />
                      </motion.div>
                    </div>
                  </div>

                  {/* After bar - PROPORTIONAL */}
                  <div>
                    <div className="flex justify-between text-xs mb-1.5">
                      <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: i * 0.2 + 0.8 }}
                        className="font-semibold" style={{ color: TEAL }}>After</motion.span>
                      <motion.span initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: i * 0.2 + 1 }}
                        className="font-bold text-sm" style={{ color: NAVY }}>
                        <AnimatedNumber value={s.afterVal} suffix={s.afterLabel.includes("min") ? " min" : "%"} inView={inView} delay={i * 200 + 800} />
                      </motion.span>
                    </div>
                    <div className="h-3 rounded-full" style={{ background: '#D1FAE5' }}>
                      <motion.div
                        initial={{ width: 0 }}
                        animate={inView ? { width: `${s.afterPct}%` } : {}}
                        transition={{ duration: 1.4, delay: i * 0.2 + 0.9, ease: "easeOut" }}
                        className="h-full rounded-full relative overflow-hidden"
                        style={{ background: TEAL }}
                      >
                        <motion.div className="absolute inset-0" style={{ background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent)' }}
                          animate={{ x: ['-100%', '100%'] }} transition={{ duration: 2, repeat: Infinity, delay: 2.5 }} />
                      </motion.div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Testimonial quotes with animated borders */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {personas.map((p, i) => (
            <motion.div key={p.name}
              initial={{ opacity: 0, y: 30, scale: 0.95 }}
              animate={inView ? { opacity: 1, y: 0, scale: 1 } : {}}
              transition={{ delay: i * 0.15 + 0.8, duration: 0.6 }}
              whileHover={{ y: -4 }}
            >
              <div className="relative rounded-2xl p-[1.5px] overflow-hidden h-full" style={{
                background: `linear-gradient(${120 + i * 60}deg, ${TEAL}30, transparent 50%, ${TEAL}30)`,
                backgroundSize: '200% 200%',
                animation: `borderShift ${5 + i}s ease infinite`,
              }}>
                <div className="bg-white rounded-2xl p-6 h-full flex flex-col">
                  <motion.div initial={{ scale: 0 }} animate={inView ? { scale: 1, rotate: [0, 10, 0] } : {}}
                    transition={{ delay: i * 0.2 + 1.2, type: "spring" }}>
                    <Quote className="w-6 h-6 mb-3" style={{ color: `${TEAL}30` }} />
                  </motion.div>
                  <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: i * 0.15 + 1 }}
                    className="text-sm leading-relaxed italic flex-1 mb-4" style={{ color: NAVY }}>"{p.quote}"</motion.p>
                  <div className="flex items-center gap-3 pt-3 border-t" style={{ borderColor: `${TEAL}15` }}>
                    <motion.img src={p.img} alt={p.name}
                      initial={{ scale: 0 }} animate={inView ? { scale: 1 } : {}} transition={{ delay: i * 0.15 + 1.2, type: "spring" }}
                      className="w-10 h-10 rounded-full object-cover" style={{ border: `2px solid ${TEAL}30` }} />
                    <div>
                      <motion.p initial={{ opacity: 0, x: -10 }} animate={inView ? { opacity: 1, x: 0 } : {}} transition={{ delay: i * 0.15 + 1.3 }}
                        className="text-sm font-heading font-bold" style={{ color: NAVY }}>{p.name}</motion.p>
                      <motion.p initial={{ opacity: 0 }} animate={inView ? { opacity: 1 } : {}} transition={{ delay: i * 0.15 + 1.4 }}
                        className="text-[11px]" style={{ color: MID }}>{p.role}</motion.p>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>

      <style>{`
        @keyframes borderShift {
          0% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
          100% { background-position: 0% 50%; }
        }
      `}</style>
    </section>
  );
};
